import { Popover as CompassPopover } from "@circleco/compass/components/Popover";
import { Popover as HeadlessPopover, Transition } from "@headlessui/react";
import { useState } from "react";

import { TippyV2 } from "@/react/components/SettingsApp/CompassCompat/Tooltip";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

import { useEmojiPickerContext } from "./EmojiPickerContext";
import { EmojiPickerWrapper } from "./EmojiPickerWrapper";
import { Header } from "./Header";
import CustomPicker from "./Pickers/Custom";
import EmojiMartPicker from "./Pickers/EmojiMart";
import { updatePickerStyles } from "./Pickers/EmojiMart/utils";

export const EmojiPopOver = () => {
  const {
    as,
    buttonAriaLabel,
    setTargetElement,
    setPopperElement,
    styles,
    attributes,
    shouldRenderEmojiMartPicker,
    isCustomPickerTabActive,
    target,
    targetWrapperClassName,
    shouldEmojiPickerRenderAsPortal,
    popoverPanelClassName,
    shouldShowEmojiPreview,
    isCustomFileUploadProcessing,
    portalTo,
  } = useEmojiPickerContext();
  const isCompassEnabled = useIsCircleV4FeatureEnabled(
    "circle_v4_popover_enabled",
  );
  const [isOpen, setIsOpen] = useState(false);

  const handlePickerVisibility = (open: boolean) => {
    if (!isCompassEnabled) {
      updatePickerStyles(portalTo?.ownerDocument);
    }
    if (
      !isCustomPickerTabActive ||
      (isCustomPickerTabActive && !isCustomFileUploadProcessing)
    ) {
      return open;
    }
    return isCustomFileUploadProcessing;
  };

  // Legacy path (Headless UI)
  if (!isCompassEnabled) {
    return (
      <HeadlessPopover as="div" data-testid="emoji-picker">
        {({ close, open }) => (
          <>
            <TippyV2
              placement="bottom"
              interactive={false}
              content={buttonAriaLabel}
              enabled={!!buttonAriaLabel}
              shouldUseSpanWrapper={false}
            >
              <HeadlessPopover.Button
                as={as}
                ref={setTargetElement}
                className={classNames("inline-flex", targetWrapperClassName)}
                data-testid="emoji-picker-button"
                aria-label={buttonAriaLabel}
              >
                {target}
              </HeadlessPopover.Button>
            </TippyV2>
            <EmojiPickerWrapper
              open={open}
              shouldEmojiPickerRenderAsPortal={shouldEmojiPickerRenderAsPortal}
              portalTo={portalTo}
            >
              <Transition
                show={handlePickerVisibility(open)}
                as="div"
                enter="transition ease-out duration-100"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <HeadlessPopover.Panel
                  ref={setPopperElement}
                  style={styles.popper}
                  {...attributes.popper}
                  className={classNames(
                    "bg-primary border-primary w-[24rem] rounded-md border shadow-lg md:w-[28rem]",
                    popoverPanelClassName,
                  )}
                  data-testid="emoji-popover-panel"
                >
                  <>
                    <Header />
                    <div
                      className={classNames({
                        "py-3": !shouldShowEmojiPreview,
                        "pt-3": shouldShowEmojiPreview,
                        "px-1": shouldRenderEmojiMartPicker,
                        "px-4 pb-3": !shouldRenderEmojiMartPicker,
                      })}
                    >
                      {shouldRenderEmojiMartPicker && (
                        <EmojiMartPicker close={close} />
                      )}
                      {isCustomPickerTabActive && (
                        <CustomPicker close={close} />
                      )}
                    </div>
                  </>
                </HeadlessPopover.Panel>
              </Transition>
            </EmojiPickerWrapper>
          </>
        )}
      </HeadlessPopover>
    );
  }

  // New path (Compass Popover)
  const isPickerVisible = handlePickerVisibility(isOpen);
  const isWrapperInteractive = as === "button";

  const triggerElement = isWrapperInteractive ? (
    <button
      type="button"
      className={classNames("inline-flex", targetWrapperClassName)}
      data-testid="emoji-picker-button"
      aria-label={buttonAriaLabel}
    >
      {target}
    </button>
  ) : (
    <div
      tabIndex={-1}
      className={classNames("inline-flex", targetWrapperClassName)}
      data-testid="emoji-picker-button"
    >
      {target}
    </div>
  );

  const WrapperTag = isWrapperInteractive ? "div" : (as ?? "div");

  const popover = (
    <WrapperTag data-testid="emoji-picker">
      <CompassPopover
        trigger={triggerElement}
        open={isPickerVisible}
        onOpenChange={setIsOpen}
        side="bottom"
        align="start"
        className="w-[24rem] md:w-[28rem] p-0.5"
      >
        <>
          <Header />
          {shouldRenderEmojiMartPicker && (
            <EmojiMartPicker close={() => setIsOpen(false)} />
          )}
          {isCustomPickerTabActive && (
            <CustomPicker close={() => setIsOpen(false)} />
          )}
        </>
      </CompassPopover>
    </WrapperTag>
  );

  if (!buttonAriaLabel) {
    return popover;
  }

  return (
    <TippyV2
      placement="bottom"
      interactive={false}
      content={buttonAriaLabel}
      shouldUseSpanWrapper={false}
      trigger="mouseenter focusin"
      delay={0}
    >
      {popover}
    </TippyV2>
  );
};
